import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AskFrankDesk, {
    DeskRibbon,
    DeskTitle,
    DeskNote,
    DeskPanel,
    DeskField,
    DeskEmpty,
    Pill,
    DESK_INPUT_CLASS,
    DESK_BUTTON_CLASS,
    DESK_PRIMARY_CLASS,
    DESK_PRIMARY_STYLE,
    TINY_BUTTON_CLASS,
    ASKFRANK_TINT,
} from '../../../../components/staff/askfrank/AskFrankDesk';
import EntityEditor from '../../../../components/staff/askfrank/EntityEditor';
import NameReview from '../../../../components/staff/askfrank/NameReview';
import {
    RECORD_TYPES,
    RECORD_ORDER,
    ENTITY_KINDS,
    ALL_KINDS,
    LEXICON_KINDS,
    EMPTY_ENTITY,
    entityFromDetail,
} from '../../../../components/staff/askfrank/knowledgeShared';
import {
    getAskFrankEntities,
    getAskFrankEntity,
    getAskFrankKeywords,
    saveAskFrankKeyword,
    deleteAskFrankKeyword,
    getAskFrankSynonyms,
    saveAskFrankSynonym,
    deleteAskFrankSynonym,
    getAskFrankLexicon,
    updateAskFrankLexiconTerm,
    getAskFrankAssociations,
    getAskFrankCandidates,
} from '../../../../services/api';

const TYPE_FILTERS = [
    { value: 'all', label: 'Everything' },
    { value: 'entry', label: 'Entries' },
    { value: 'keyword', label: 'Keywords' },
    { value: 'jargon', label: 'Jargon' },
    { value: 'term', label: 'Terms' },
];

const EMPTY_KEYWORD = { keyword: '', entityId: '', weight: 1, note: '', enabled: true };
const EMPTY_JARGON = { term: '', expansion: '', weight: 0.7, enabled: true };

function TypeTab({ option, active, onSelect, count }) {
    return (
        <button
            type="button"
            onClick={() => onSelect(option.value)}
            className={`border px-3 py-[5px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] transition-colors ${
                active ? 'border-black/30 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
            }`}
            style={active ? { backgroundColor: ASKFRANK_TINT } : undefined}
        >
            {option.label}
            {count !== null && count !== undefined && (
                <span className="ml-2 text-[12px] opacity-70">{count}</span>
            )}
        </button>
    );
}

function Row({ row, selected, onSelect }) {
    const type = RECORD_TYPES[row.type];

    return (
        <button
            type="button"
            onClick={() => onSelect(row)}
            className={`flex w-full flex-wrap items-center gap-2 border-b border-gray-200 px-3 py-2 text-left last:border-b-0 ${
                selected ? 'bg-offwhite' : 'bg-white hover:bg-gray-50'
            }`}
        >
            <Pill tone={type.tone}>{type.label}</Pill>

            <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] text-gray-900">{row.title}</p>
                <p className="truncate text-[11px] text-gray-400">{row.subtitle}</p>
            </div>

            {row.kind && <span className="shrink-0 text-[10px] uppercase text-gray-400">{row.kind}</span>}
            {row.badges?.map(badge => <Pill key={badge} tone="yellow">{badge}</Pill>)}
            {!row.enabled && <Pill tone="red">off</Pill>}

            <span className="hidden shrink-0 text-[11px] text-gray-400 sm:block">{row.meta}</span>
        </button>
    );
}

function SenseGroup({ group, open, onToggle, selectedId, onSelect }) {
    return (
        <div className="border-b border-gray-200 last:border-b-0">
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full flex-wrap items-center gap-2 bg-white px-3 py-2 text-left hover:bg-gray-50"
            >
                <Pill tone="green">Entry</Pill>

                <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] text-gray-900">{group.title}</p>
                    <p className="truncate text-[11px] text-gray-400">
                        Means {group.rows.length} different things: {group.rows.map(row => row.kind).join(', ')}
                    </p>
                </div>

                <Pill tone="orange">{group.rows.length} senses</Pill>
                <span className="shrink-0 text-[11px] text-gray-400">{open ? 'Hide' : 'Show'}</span>
            </button>

            {open && (
                <div className="border-t border-gray-200 bg-gray-50 pl-6">
                    {group.rows.map(row => (
                        <Row
                            key={`${row.type}-${row.id}`}
                            row={row}
                            selected={selectedId === row.id}
                            onSelect={onSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function KeywordForm({ value, entities, onChange, onSave, onDelete, onCancel, busy }) {
    return (
        <div className="flex flex-col gap-3">
            <DeskField label="Keyword or phrase" required>
                <input
                    type="text"
                    value={value.keyword}
                    onChange={(e) => onChange({ ...value, keyword: e.target.value })}
                    placeholder="public room"
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Always answer with" hint="the entry this word pins to">
                <select
                    value={value.entityId || ''}
                    onChange={(e) => onChange({ ...value, entityId: e.target.value })}
                    className={DESK_INPUT_CLASS}
                >
                    <option value="">Nothing pinned</option>
                    {entities.map(entity => (
                        <option key={entity.id} value={entity.id}>{entity.name} ({entity.kind})</option>
                    ))}
                </select>
            </DeskField>

            <DeskField label="Weight" hint="higher wins when two keywords match">
                <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="9.9"
                    value={value.weight}
                    onChange={(e) => onChange({ ...value, weight: Number(e.target.value) })}
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Note" hint="why this keyword exists">
                <input
                    type="text"
                    value={value.note || ''}
                    onChange={(e) => onChange({ ...value, note: e.target.value })}
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <label className="flex items-center gap-2 text-[12px] text-gray-700">
                <input
                    type="checkbox"
                    checked={value.enabled}
                    onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
                />
                In use
            </label>

            <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={onSave} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    Save keyword
                </button>
                <button type="button" onClick={onCancel} className={DESK_BUTTON_CLASS}>Cancel</button>
                {onDelete && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onDelete}
                        className="ml-auto bg-red-700 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white disabled:opacity-50"
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}

function JargonForm({ value, onChange, onSave, onDelete, onCancel, busy }) {
    return (
        <div className="flex flex-col gap-3">
            <DeskField label="Term" required hint="what people type">
                <input
                    type="text"
                    value={value.term}
                    onChange={(e) => onChange({ ...value, term: e.target.value })}
                    placeholder="hc"
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Also search for" required hint="what Frank should look for as well">
                <input
                    type="text"
                    value={value.expansion}
                    onChange={(e) => onChange({ ...value, expansion: e.target.value })}
                    placeholder="habbo club"
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <DeskField label="Weight" hint="how much the extra words count, 1 is as much as the original">
                <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="2"
                    value={value.weight}
                    onChange={(e) => onChange({ ...value, weight: Number(e.target.value) })}
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <label className="flex items-center gap-2 text-[12px] text-gray-700">
                <input
                    type="checkbox"
                    checked={value.enabled}
                    onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
                />
                In use
            </label>

            <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={onSave} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    Save jargon
                </button>
                <button type="button" onClick={onCancel} className={DESK_BUTTON_CLASS}>Cancel</button>
                {onDelete && (
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onDelete}
                        className="ml-auto bg-red-700 px-4 py-[6px] font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-white disabled:opacity-50"
                    >
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}

function TermForm({ term, onSaved, onCancel }) {
    const [value, setValue] = useState(term);
    const [related, setRelated] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setValue(term);
        setRelated(null);

        getAskFrankAssociations(term.term)
            .then(res => setRelated(res.success ? (res.related || []) : []))
            .catch(() => setRelated([]));
    }, [term]);

    const save = async () => {
        setBusy(true);
        const res = await updateAskFrankLexiconTerm(term.id, {
            kind: value.kind,
            weight: value.weight,
            enabled: value.enabled,
            locked: value.locked,
        });
        setBusy(false);

        if (res.success) {
            toast.success('Term saved');
            onSaved();
        } else {
            toast.error(res.message || 'Could not change that term.');
        }
    };

    return (
        <div className="flex flex-col gap-3">
            <div className="border border-gray-200 bg-white px-3 py-2">
                <p className="font-['Jersey_10'] text-[22px] uppercase leading-none tracking-[1px] text-gray-900">
                    {term.term}
                </p>
                <p className="mt-1 text-[11px] text-gray-500">
                    {term.memberCount} items, seen on {term.docFreq} pages, built by {term.origin}
                </p>
            </div>

            <DeskField label="Kind" hint="what sort of thing this word points at">
                <select
                    value={value.kind || ''}
                    onChange={(e) => setValue({ ...value, kind: e.target.value })}
                    className={DESK_INPUT_CLASS}
                >
                    {LEXICON_KINDS.map(kind => <option key={kind} value={kind}>{kind}</option>)}
                </select>
            </DeskField>

            <DeskField label="Weight" hint="how hard this term pulls a search">
                <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={value.weight}
                    onChange={(e) => setValue({ ...value, weight: Number(e.target.value) })}
                    className={DESK_INPUT_CLASS}
                />
            </DeskField>

            <label className="flex items-center gap-2 text-[12px] text-gray-700">
                <input
                    type="checkbox"
                    checked={value.enabled}
                    onChange={(e) => setValue({ ...value, enabled: e.target.checked })}
                />
                In use
            </label>

            <label className="flex items-start gap-2 text-[12px] text-gray-700">
                <input
                    type="checkbox"
                    checked={value.locked}
                    onChange={(e) => setValue({ ...value, locked: e.target.checked })}
                />
                <span>Locked, so the next rebuild in Lab leaves your changes alone</span>
            </label>

            <div className="flex flex-wrap gap-2">
                <button type="button" disabled={busy} onClick={save} className={DESK_PRIMARY_CLASS} style={DESK_PRIMARY_STYLE}>
                    Save term
                </button>
                <button type="button" onClick={onCancel} className={DESK_BUTTON_CLASS}>Cancel</button>
            </div>

            <div className="border border-gray-200 bg-white p-2">
                <p className="font-['Jersey_10'] text-[14px] uppercase tracking-[1px] text-gray-600">
                    Words Frank has learned sit near this one
                </p>
                <p className="mb-2 text-[11px] text-gray-500">
                    Worked out from the pages he has read. This is what turns a question about fishing into results about
                    rods and derbies without anybody typing a list.
                </p>

                {related === null ? (
                    <p className="text-[12px] text-gray-400">Reading...</p>
                ) : related.length === 0 ? (
                    <p className="text-[12px] text-gray-400">Nothing sits near it yet.</p>
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {related.map(entry => (
                            <span
                                key={entry.term}
                                title={`PMI ${entry.pmi.toFixed(2)} across ${entry.coDocs} pages`}
                                className="border border-gray-300 bg-offwhite px-2 py-[2px] text-[12px]"
                            >
                                {entry.term}
                                <span className="ml-1 text-[10px] text-gray-400">{entry.pmi.toFixed(1)}</span>
                            </span>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function AskFrankKnowledge({ userData }) {
    const [typeFilter, setTypeFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [kindFilter, setKindFilter] = useState('');
    const [showFilter, setShowFilter] = useState('');
    const [termPage, setTermPage] = useState(1);

    const [entities, setEntities] = useState([]);
    const [entityTotals, setEntityTotals] = useState({});
    const [keywords, setKeywords] = useState([]);
    const [jargon, setJargon] = useState([]);
    const [lexicon, setLexicon] = useState({ terms: [], total: 0, perPage: 60 });
    const [candidates, setCandidates] = useState([]);

    const [openSenses, setOpenSenses] = useState([]);
    const [selected, setSelected] = useState(null);
    const [draft, setDraft] = useState(null);
    const [entityDetail, setEntityDetail] = useState(null);
    const [reviewing, setReviewing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    const wantsTerms = typeFilter === 'all' || typeFilter === 'term';

    const load = useCallback(async () => {
        const [entityRes, keywordRes, jargonRes, candidateRes, lexiconRes] = await Promise.all([
            getAskFrankEntities({ q: search, kind: ENTITY_KINDS.some(k => k.value === kindFilter) ? kindFilter : '' }),
            getAskFrankKeywords(),
            getAskFrankSynonyms(),
            getAskFrankCandidates('new'),
            wantsTerms
                ? getAskFrankLexicon({ q: search, kind: LEXICON_KINDS.includes(kindFilter) ? kindFilter : '', page: termPage })
                : Promise.resolve({ success: false }),
        ]);

        if (entityRes.success) {
            setEntities(entityRes.entities || []);
            setEntityTotals(entityRes.totals || {});
        }
        if (keywordRes.success) setKeywords(keywordRes.keywords || []);
        if (jargonRes.success) setJargon(jargonRes.synonyms || []);
        if (candidateRes.success) setCandidates(candidateRes.candidates || []);
        if (lexiconRes.success) setLexicon(lexiconRes);

        setLoading(false);
    }, [search, kindFilter, termPage, wantsTerms]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => { setTermPage(1); }, [search, kindFilter]);

    if (userData && !userData.hasAdminRole) return <Navigate to="/staff" replace />;

    const matches = (text) => !search || String(text || '').toLowerCase().includes(search.toLowerCase());
    const passesShow = (enabled) => showFilter === '' || (showFilter === 'on' ? enabled : !enabled);

    const entityKindPicked = !kindFilter || ENTITY_KINDS.some(kind => kind.value === kindFilter);
    const lexiconKindPicked = !kindFilter || LEXICON_KINDS.includes(kindFilter);

    const entryRows = !entityKindPicked ? [] : entities
        .filter(entity => passesShow(entity.status === 'active'))
        .map(entity => ({
            type: 'entry',
            id: entity.id,
            title: entity.name,
            subtitle: entity.headline || entity.summary || 'No description yet.',
            kind: entity.kind,
            enabled: entity.status === 'active',
            badges: [
                entity.status !== 'active' ? entity.status : null,
                entity.protected ? 'curated only' : null,
            ].filter(Boolean),
            meta: `${entity.mentionCount} mentions | ${entity.aliasCount} linked | ${entity.factCount} facts`,
            raw: entity,
        }));

    const keywordRows = kindFilter ? [] : keywords
        .filter(keyword => matches(`${keyword.keyword} ${keyword.entityName || ''} ${keyword.note || ''}`))
        .filter(keyword => passesShow(keyword.enabled))
        .map(keyword => ({
            type: 'keyword',
            id: keyword.id,
            title: keyword.keyword,
            subtitle: keyword.entityName ? `Always answers with ${keyword.entityName}` : 'Nothing pinned yet',
            kind: null,
            enabled: keyword.enabled,
            badges: [],
            meta: `x${keyword.weight}`,
            raw: keyword,
        }));

    const jargonRows = kindFilter ? [] : jargon
        .filter(row => matches(`${row.term} ${row.expansion}`))
        .filter(row => passesShow(row.enabled))
        .map(row => ({
            type: 'jargon',
            id: row.id,
            title: row.term,
            subtitle: `Also searches for ${row.expansion}`,
            kind: null,
            enabled: row.enabled,
            badges: [],
            meta: `x${row.weight}`,
            raw: row,
        }));

    const termRows = !lexiconKindPicked ? [] : (lexicon.terms || [])
        .filter(term => passesShow(term.enabled))
        .map(term => ({
            type: 'term',
            id: term.id,
            title: term.term,
            subtitle: `${term.memberCount} items, seen on ${term.docFreq} pages`,
            kind: term.kind,
            enabled: term.enabled,
            badges: term.locked ? ['locked'] : [],
            meta: `x${term.weight} | ${term.origin}`,
            raw: term,
        }));

    const entryGroups = [];
    const byName = new Map();

    entryRows.forEach(row => {
        const key = row.raw.normalised || row.id;
        byName.set(key, [...(byName.get(key) || []), row]);
    });

    byName.forEach((rows, key) => {
        if (rows.length === 1) {
            entryGroups.push(rows[0]);
            return;
        }

        entryGroups.push({
            type: 'sensegroup',
            id: key,
            title: rows[0].title,
            rows,
        });
    });

    const byType = {
        entry: entryGroups,
        keyword: keywordRows,
        jargon: jargonRows,
        term: termRows,
    };

    const groups = (typeFilter === 'all' ? RECORD_ORDER : [typeFilter])
        .map(key => ({ key, rows: byType[key] }))
        .filter(group => group.rows.length > 0);

    const shownCount = groups.reduce(
        (total, group) => total + group.rows.reduce(
            (rows, row) => rows + (row.type === 'sensegroup' ? row.rows.length : 1),
            0
        ),
        0
    );
    const termPages = Math.max(1, Math.ceil((lexicon.total || 0) / (lexicon.perPage || 60)));

    const openRow = async (row) => {
        setDraft(null);

        if (row.type === 'entry') {
            setSelected(row);
            setEntityDetail(null);

            const res = await getAskFrankEntity(row.id);
            if (res.success) setEntityDetail(entityFromDetail(res));
            else toast.error(res.message || 'Could not open that entry');
            return;
        }

        setSelected(row);
        setEntityDetail(null);
    };

    const startNew = (type) => {
        if (!type) return;

        setSelected({ type, id: null, title: 'New' });
        setEntityDetail(type === 'entry' ? EMPTY_ENTITY : null);
        setDraft(type === 'keyword' ? EMPTY_KEYWORD : type === 'jargon' ? EMPTY_JARGON : null);
    };

    const close = () => {
        setSelected(null);
        setEntityDetail(null);
        setDraft(null);
    };

    const runSave = async (fn, message) => {
        setBusy(true);
        const res = await fn();
        setBusy(false);

        if (res.success) {
            toast.success(res.message || message);
            close();
            await load();
        } else {
            toast.error(res.message || 'That did not work');
        }
    };

    const detailFor = () => {
        if (!selected) return null;

        const type = RECORD_TYPES[selected.type];

        if (selected.type === 'entry') {
            if (!entityDetail) return <DeskEmpty>Opening...</DeskEmpty>;

            return (
                <EntityEditor
                    key={selected.id || 'new'}
                    entityId={selected.id}
                    initial={entityDetail}
                    onSaved={() => { close(); load(); }}
                    onDeleted={() => { close(); load(); }}
                    onCancel={close}
                />
            );
        }

        if (selected.type === 'keyword') {
            const value = draft || {
                keyword: selected.raw.keyword,
                entityId: selected.raw.entityId || '',
                weight: selected.raw.weight,
                note: selected.raw.note || '',
                enabled: selected.raw.enabled,
            };

            return (
                <KeywordForm
                    value={value}
                    entities={entities}
                    busy={busy}
                    onChange={setDraft}
                    onCancel={close}
                    onSave={() => {
                        if (!value.keyword.trim()) { toast.error('Enter a keyword'); return; }
                        runSave(
                            () => saveAskFrankKeyword(selected.id, { ...value, entityId: value.entityId || null }),
                            'Keyword saved'
                        );
                    }}
                    onDelete={selected.id ? () => runSave(() => deleteAskFrankKeyword(selected.id), 'Keyword removed') : null}
                />
            );
        }

        if (selected.type === 'jargon') {
            const value = draft || {
                term: selected.raw.term,
                expansion: selected.raw.expansion,
                weight: selected.raw.weight,
                enabled: selected.raw.enabled,
            };

            return (
                <JargonForm
                    value={value}
                    busy={busy}
                    onChange={setDraft}
                    onCancel={close}
                    onSave={() => {
                        if (!value.term.trim() || !value.expansion.trim()) {
                            toast.error('A term and an expansion are required');
                            return;
                        }
                        runSave(() => saveAskFrankSynonym(selected.id, value), 'Jargon saved');
                    }}
                    onDelete={selected.id ? () => runSave(() => deleteAskFrankSynonym(selected.id), 'Jargon removed') : null}
                />
            );
        }

        if (selected.type === 'term') {
            return (
                <TermForm
                    key={selected.id}
                    term={selected.raw}
                    onSaved={() => { close(); load(); }}
                    onCancel={close}
                />
            );
        }

        return <DeskEmpty>{type.blurb}</DeskEmpty>;
    };

    if (reviewing) {
        return (
            <AskFrankDesk userData={userData}>
                <NameReview
                    candidates={candidates}
                    onClose={() => { setReviewing(false); load(); }}
                />
            </AskFrankDesk>
        );
    }

    return (
        <AskFrankDesk userData={userData}>
            <DeskRibbon>
                <DeskTitle>What Frank Knows</DeskTitle>

                <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search everything"
                    className="w-[200px] border border-gray-300 px-2 py-[5px] text-[13px]"
                />

                <select
                    value={kindFilter}
                    onChange={(e) => setKindFilter(e.target.value)}
                    className="border border-gray-300 px-2 py-[5px] text-[13px]"
                >
                    <option value="">Any kind</option>
                    {ALL_KINDS.map(kind => <option key={kind} value={kind}>{kind}</option>)}
                </select>

                <select
                    value={showFilter}
                    onChange={(e) => setShowFilter(e.target.value)}
                    className="border border-gray-300 px-2 py-[5px] text-[13px]"
                >
                    <option value="">In use and not</option>
                    <option value="on">In use only</option>
                    <option value="off">Switched off only</option>
                </select>

                <select
                    value=""
                    onChange={(e) => startNew(e.target.value)}
                    className="ml-auto border border-gray-300 px-2 py-[5px] text-[13px]"
                >
                    <option value="">Add something...</option>
                    <option value="entry">New entry</option>
                    <option value="keyword">New keyword</option>
                    <option value="jargon">New jargon</option>
                </select>

                <button
                    type="button"
                    onClick={() => setReviewing(true)}
                    className={DESK_PRIMARY_CLASS}
                    style={DESK_PRIMARY_STYLE}
                >
                    Review names{candidates.length > 0 ? ` (${candidates.length})` : ''}
                </button>
            </DeskRibbon>

            <div className="flex flex-wrap items-center gap-1">
                {TYPE_FILTERS.map(option => (
                    <TypeTab
                        key={option.value}
                        option={option}
                        active={typeFilter === option.value}
                        onSelect={setTypeFilter}
                        count={option.value === 'all'
                            ? null
                            : option.value === 'entry' ? entryRows.length : (byType[option.value] || []).length}
                    />
                ))}

                <DeskNote>
                    {typeFilter === 'all'
                        ? `Everything Frank has been taught. ${entityTotals.total || 0} entries, ${keywords.length} keywords, ${jargon.length} jargon, ${lexicon.total || 0} terms.`
                        : RECORD_TYPES[typeFilter].blurb}
                </DeskNote>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
                <div className={selected ? 'xl:col-span-7' : 'xl:col-span-12'}>
                    <DeskPanel
                        title="Knowledge"
                        padding="p-0"
                        right={<span className="text-[11px] uppercase text-white/70">{shownCount} shown</span>}
                    >
                        {loading ? (
                            <DeskEmpty>Reading the shelves...</DeskEmpty>
                        ) : groups.length === 0 ? (
                            <DeskEmpty>Nothing matches that.</DeskEmpty>
                        ) : (
                            <div className="max-h-[640px] overflow-y-auto">
                                {groups.map(group => (
                                    <div key={group.key}>
                                        {typeFilter === 'all' && (
                                            <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-300 bg-gray-100 px-3 py-1">
                                                <span className="font-['Jersey_10'] text-[15px] uppercase tracking-[1px] text-gray-700">
                                                    {RECORD_TYPES[group.key].plural}
                                                </span>
                                                <span className="text-[11px] text-gray-400">{group.rows.length}</span>
                                            </div>
                                        )}

                                        {group.rows.map(row => (row.type === 'sensegroup' ? (
                                            <SenseGroup
                                                key={`sense-${row.id}`}
                                                group={row}
                                                open={openSenses.includes(row.id)}
                                                onToggle={() => setOpenSenses(current => (current.includes(row.id)
                                                    ? current.filter(id => id !== row.id)
                                                    : [...current, row.id]))}
                                                selectedId={selected?.type === 'entry' ? selected.id : null}
                                                onSelect={openRow}
                                            />
                                        ) : (
                                            <Row
                                                key={`${row.type}-${row.id}`}
                                                row={row}
                                                selected={selected?.type === row.type && selected?.id === row.id}
                                                onSelect={openRow}
                                            />
                                        )))}
                                    </div>
                                ))}
                            </div>
                        )}

                        {wantsTerms && termPages > 1 && (
                            <div className="flex items-center justify-center gap-2 border-t border-gray-200 px-3 py-2">
                                <button
                                    type="button"
                                    onClick={() => setTermPage(p => Math.max(1, p - 1))}
                                    disabled={termPage <= 1}
                                    className={TINY_BUTTON_CLASS}
                                >
                                    Prev
                                </button>
                                <span className="text-[11px] text-gray-500">
                                    Terms page {termPage} of {termPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setTermPage(p => Math.min(termPages, p + 1))}
                                    disabled={termPage >= termPages}
                                    className={TINY_BUTTON_CLASS}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </DeskPanel>
                </div>

                {selected && (
                    <div className="xl:col-span-5">
                        <DeskPanel
                            title={selected.id ? selected.title : `New ${RECORD_TYPES[selected.type].label.toLowerCase()}`}
                            right={
                                <button type="button" onClick={close} className="text-[11px] uppercase text-white/80 hover:text-white">
                                    Close
                                </button>
                            }
                        >
                            <p className="mb-3 border-b border-gray-200 pb-2 text-[12px] text-gray-500">
                                {RECORD_TYPES[selected.type].blurb}
                            </p>

                            {detailFor()}
                        </DeskPanel>
                    </div>
                )}
            </div>

            {!selected && candidates.length > 0 && (
                <DeskPanel title="Names waiting">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-[13px] text-gray-700">
                            Frank has spotted {candidates.length} name{candidates.length === 1 ? '' : 's'} he does not
                            know yet, starting with <strong>{candidates[0].name}</strong>.
                        </span>

                        <button
                            type="button"
                            onClick={() => setReviewing(true)}
                            className={`${DESK_PRIMARY_CLASS} ml-auto`}
                            style={DESK_PRIMARY_STYLE}
                        >
                            Go through them
                        </button>
                    </div>
                </DeskPanel>
            )}
        </AskFrankDesk>
    );
}

export default AskFrankKnowledge;
